import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'

type BaseProps = { label: string; error?: string; hint?: string; children?: ReactNode }

export function FormField({ label, error, hint, children }: BaseProps) {
  void error
  void hint
  return (
    <div className="form-field">
      {children}
      <span className="sr-only">{label}</span>
    </div>
  )
}

export function Input({
  label,
  error,
  hint,
  id,
  ...props
}: BaseProps & InputHTMLAttributes<HTMLInputElement>) {
  const errorId = `${id}-error`
  const hintId = `${id}-hint`
  return (
    <FormField label={label}>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : hint ? hintId : undefined}
        {...props}
      />
      {error ? (
        <p id={errorId} className="form-error" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="form-hint">
          {hint}
        </p>
      ) : null}
    </FormField>
  )
}

export function Select({
  label,
  error,
  hint,
  id,
  children,
  ...props
}: BaseProps & SelectHTMLAttributes<HTMLSelectElement>) {
  const errorId = `${id}-error`
  const hintId = `${id}-hint`
  return (
    <FormField label={label}>
      <label htmlFor={id}>{label}</label>
      <select
        id={id}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : hint ? hintId : undefined}
        {...props}
      >
        {children}
      </select>
      {error ? (
        <p id={errorId} className="form-error" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="form-hint">
          {hint}
        </p>
      ) : null}
    </FormField>
  )
}

export function Textarea({
  label,
  error,
  hint,
  id,
  ...props
}: BaseProps & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const errorId = `${id}-error`
  const hintId = `${id}-hint`
  return (
    <FormField label={label}>
      <label htmlFor={id}>{label}</label>
      <textarea
        id={id}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : hint ? hintId : undefined}
        {...props}
      />
      {error ? (
        <p id={errorId} className="form-error" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="form-hint">
          {hint}
        </p>
      ) : null}
    </FormField>
  )
}
